import { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Stack, Snackbar, Alert, Tabs, Tab, useMediaQuery, Fab, Drawer, Badge, CircularProgress } from '@mui/material';
import { Map as MapIcon, CameraAlt, FormatListBulleted } from '@mui/icons-material';
import './App.css';
import { getFromDB, auth, setUnauthorizedHandler } from './Common';
import { parseCartellone, filterItems, SORTS, distanceM, votoLabel, streetViewUrl, impostaConcessionarie } from './lib/parse';
import TopBar from './components/TopBar';
import KpiBar from './components/KpiBar';
import MapView from './components/MapView';
import ListPanel from './components/ListPanel';
import DetailPanel from './components/DetailPanel';
import ScanView from './components/ScanView';
import Login from './components/Login';
import PasswordDialog from './components/PasswordDialog';
import UsersDialog from './components/UsersDialog';
import RichiesteEliminazione from './components/RichiesteEliminazione';

const EMPTY_FILTERS = { search: '', concessionarie: [], votoMin: null, stato: null, tags: [] };

export default function App() {
  const isMobile = useMediaQuery('(max-width:900px)');
  const [tab, setTab] = useState(() => window.location.hash === '#scan' ? 'scan' : 'map');
  const [positions, setPositions] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState('voto-desc');
  const [selected, setSelected] = useState(new Set());
  const [cluster, setCluster] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [map, setMap] = useState(null);
  const [toast, setToast] = useState(null);
  const [userPos, setUserPos] = useState(null);   // posizione per "Più vicini a me"
  const [me, setMe] = useState(undefined);        // undefined = sto ancora verificando la sessione
  const [avvisoLogin, setAvvisoLogin] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [richiesteOpen, setRichiesteOpen] = useState(false);
  // arrivati dal link dell'email per reimpostare la password: si mostra quella, anche con una sessione aperta
  const [resetInCorso, setResetInCorso] = useState(() => new URLSearchParams(window.location.search).has('reimposta'));

  const notify = useCallback((msg, severity = 'info') => setToast({ msg, severity }), []);

  const loadData = useCallback(() => {
    getFromDB(
      (pos) => setPositions(pos),
      () => notify('Errore nel caricamento dei cartelloni — controlla la connessione e riprova', 'error'),
    );
  }, [notify]);

  // Chi sono? Alla partenza si chiede al server: il cookie di sessione dura 90
  // giorni, quindi di norma sul telefono non si rivede mai la schermata di accesso.
  useEffect(() => {
    auth.me().then(setMe).catch((e) => { setMe(null); if (e?.codice === 'contratto') setAvvisoLogin(e.message); });
  }, []);

  // Sessione scaduta o revocata durante l'uso: si torna all'accesso, ovunque si sia.
  useEffect(() => {
    setUnauthorizedHandler((motivo) => {
      setMe(null);
      setAvvisoLogin(motivo || 'La sessione è scaduta: accedi di nuovo per continuare.');
    });
  }, []);

  const loggato = !!me && !me.deveCambiare;
  useEffect(() => { if (loggato) loadData(); }, [loggato, loadData]);

  // Cosa può fare chi è entrato: lo decide il server (me.permessi), qui si legge soltanto.
  const vedeVoto = me?.permessi?.vedeVoto !== false;
  const censisce = me?.permessi?.censisce !== false;
  // chi non vede il voto non può ordinare né filtrare per voto o stato (che dal voto dipende)
  useEffect(() => {
    if (!vedeVoto) {
      setSortKey(k => (['voto-desc', 'voto-asc', 'da-rivedere'].includes(k) ? 'recenti' : k));
      setFilters(f => ({ ...f, votoMin: null, stato: null, concessionarie: [] }));
    }
  }, [vedeVoto]);
  useEffect(() => { if (!censisce && tab === 'scan') setTab('map'); }, [censisce, tab]);
  // concessionario in etichetta e scheda: a noi e alle agenzie che lo prevedono (al concessionario no: è lui)
  const mostraConc = me?.permessi?.vedeConcessionario !== false && me?.azienda?.tipo !== 'concessionario';
  const etichettaTotale = !me?.azienda || me.azienda.tipo === 'interna' ? null
    : me.azienda.tipo === 'concessionario' ? 'I tuoi impianti' : 'Impianti';

  // contratto in scadenza: lo si ricorda all'ingresso, negli ultimi 15 giorni
  useEffect(() => {
    const al = me?.azienda?.accessoAl;
    if (!loggato || !al) return;
    const giorni = Math.round((new Date(`${al}T23:59:59`) - new Date()) / 86400000);
    if (giorni <= 15)
      notify(`L'accesso di ${me.azienda.nome} scade il ${al.split('-').reverse().join('/')}: per rinnovarlo contatta AffariAffissioni.`, 'warning');
  }, [loggato]);   // eslint-disable-line react-hooks/exhaustive-deps -- una volta per accesso

  // Concessionari registrati (solo per chi vede tutto): riconosciuti nei titoli e nei
  // filtri, e proposti all'amministratore per l'assegnazione dalla scheda.
  const [concessionari, setConcessionari] = useState([]);
  const vedeTutto = me?.permessi?.vedeTutto !== false;
  const caricaConcessionari = useCallback(() => {
    auth.concessionari().then((l) => { impostaConcessionarie(l.map(c => c.nome)); setConcessionari(l); }).catch(() => {});
  }, []);
  useEffect(() => { if (loggato && vedeTutto) caricaConcessionari(); }, [loggato, vedeTutto, caricaConcessionari]);
  useEffect(() => { window.location.hash = tab === 'scan' ? '#scan' : '#map'; }, [tab]);

  const esci = useCallback(async () => {
    try { await auth.logout(); } catch { /* il cookie sparisce comunque */ }
    setMe(null);
    setAvvisoLogin(null);
  }, []);

  // quando si sceglie "Più vicini a me", acquisisci la posizione
  useEffect(() => {
    if (sortKey !== 'vicini') return;
    if (!navigator.geolocation) { notify('Geolocalizzazione non disponibile su questo dispositivo', 'error'); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setUserPos({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => notify('Posizione non disponibile — abilita il GPS per ordinare per vicinanza', 'error'),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
    );
  }, [sortKey, notify]);

  // si rianalizza anche quando cambia l'elenco dei concessionari riconosciuti
  const items = useMemo(() => positions.map(p => ({ raw: p, meta: parseCartellone(p) })), [positions, concessionari]);
  // richieste di eliminazione che aspettano un amministratore (arrivano dentro gli impianti)
  const richieste = useMemo(() => (me?.amministratore ? items.filter(i => i.raw.eliminazione?.stato === 'attesa') : []),
                            [items, me]);

  const comparator = useMemo(() => {
    if (sortKey === 'vicini' && userPos) {
      return (a, b) => distanceM(userPos.lat, userPos.lon, a.raw.latitude, a.raw.longitude)
                     - distanceM(userPos.lat, userPos.lon, b.raw.latitude, b.raw.longitude);
    }
    return SORTS[sortKey].fn;
  }, [sortKey, userPos]);

  const shown = useMemo(() => {
    const f = filterItems(items, filters);
    return [...f].sort(comparator);
  }, [items, filters, comparator]);

  const detailItem = useMemo(() => items.find(i => i.raw.id === detailId) || null, [items, detailId]);

  /** Export Excel dei selezionati: 5 colonne essenziali, nell'ordinamento attivo. */
  const exportSelectedXlsx = useCallback(async () => {
    const sel = items.filter(i => selected.has(i.raw.id)).sort(comparator);
    if (!sel.length) return;
    try {
      const XLSX = await import('xlsx');
      const rows = sel.map(({ raw, meta }) => ({
        'Titolo': raw.title || `id ${raw.id}`,
        ...(vedeVoto ? { 'Voto': votoLabel(meta.voto) } : {}),
        'Indirizzo': raw.nomination || '',
        'Link Street View': streetViewUrl(raw),
        'Latitudine': raw.latitude,
        'Longitudine': raw.longitude,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 45 }, ...(vedeVoto ? [{ wch: 6 }] : []), { wch: 55 }, { wch: 60 }, { wch: 12 }, { wch: 12 }];
      const colSv = Object.keys(rows[0]).indexOf('Link Street View');
      rows.forEach((r, i) => {   // link Street View cliccabili in Excel
        const cell = ws[XLSX.utils.encode_cell({ r: i + 1, c: colSv })];
        if (cell?.v) cell.l = { Target: String(cell.v), Tooltip: 'Apri Street View' };
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Cartelloni');
      XLSX.writeFile(wb, `cartelloni-selezionati-${new Date().toISOString().slice(0, 10)}.xlsx`);
      notify(`Excel esportato: ${sel.length} cartelloni ✓`, 'success');
    } catch {
      notify("Errore nell'export Excel", 'error');
    }
  }, [items, selected, comparator, notify, vedeVoto]);

  const openFromList = useCallback((id) => {
    const it = items.find(i => i.raw.id === id);
    if (it && map && it.raw.latitude != null) map.flyTo([it.raw.latitude, it.raw.longitude], 15);
    setDetailId(id);
    if (isMobile) setListOpen(false);
  }, [items, map, isMobile]);

  /* --- cancello: prima di tutto, chi sta usando l'app ------------------- */
  if (me === undefined) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100dvh', bgcolor: 'background.default' }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (!me || resetInCorso) {
    // Dopo un accesso si torna sempre alla mappa: l'indirizzo conserva l'ultima
    // scheda usata (#scan), e senza questo ci si ritroverebbe davanti alla
    // fotocamera appena entrati, invece che davanti al quadro d'insieme.
    return <Login messaggio={avvisoLogin}
                  onEntrato={(u) => { setAvvisoLogin(null); setTab('map'); setMe(u); }}
                  // reimpostata la password le sessioni si chiudono: si torna all'accesso
                  onFineReset={(fatto) => { setResetInCorso(false); if (fatto) setMe(null); }} />;
  }
  if (me.deveCambiare) {
    return <Login utenteDaAggiornare={me} onEntrato={setMe} />;
  }

  return (
    <Stack sx={{ height: '100dvh', bgcolor: 'background.default' }}>
      <TopBar filters={filters} setFilters={setFilters} sortKey={sortKey} setSortKey={setSortKey}
              selected={selected} onClear={() => setSelected(new Set())} onRefresh={loadData}
              onExportSelected={exportSelectedXlsx} isMobile={isMobile}
              me={me} onLogout={esci} onCambiaPassword={() => setPwOpen(true)}
              onGestisciUtenti={() => setUsersOpen(true)}
              nRichieste={richieste.length} onRichieste={() => setRichiesteOpen(true)} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: 'white', borderBottom: '1px solid #e2e8f0', minHeight: 42 }}>
        <Tab icon={<MapIcon fontSize="small" />} iconPosition="start" label="Mappa" value="map" sx={{ minHeight: 42 }} />
        {censisce && <Tab icon={<CameraAlt fontSize="small" />} iconPosition="start" label="Scan" value="scan" sx={{ minHeight: 42 }} />}
      </Tabs>

      {tab === 'map' && (
        <>
          <KpiBar items={items} filtered={shown.length} filters={filters} setFilters={setFilters} vedeVoto={vedeVoto}
                  etichettaTotale={etichettaTotale} />
          <Stack direction="row" sx={{ flex: 1, minHeight: 0, p: 1.5, pt: 1, gap: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0, borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <MapView items={shown} onOpen={setDetailId} setMap={setMap}
                       cluster={cluster} setCluster={setCluster} userPos={userPos} height="100%"
                       onPosizione={setUserPos} onErrore={(msg) => notify(msg, 'error')} vedeVoto={vedeVoto} />
            </Box>
            {!isMobile && (
              <Box sx={{ width: 400, flexShrink: 0, bgcolor: 'white', borderRadius: 2, border: '1px solid #e2e8f0', minHeight: 0 }}>
                <ListPanel items={shown} selected={selected} setSelected={setSelected} onOpen={openFromList}
                           userPos={sortKey === 'vicini' ? userPos : null} vedeVoto={vedeVoto} mostraConc={mostraConc} />
              </Box>
            )}
          </Stack>
          {isMobile && (
            <>
              <Fab color="primary" variant="extended" onClick={() => setListOpen(true)}
                   sx={{ position: 'fixed', bottom: 18, right: 16, zIndex: 1100 }}>
                <Badge badgeContent={selected.size} color="warning" sx={{ mr: 1 }}>
                  <FormatListBulleted />
                </Badge>
                Lista ({shown.length})
              </Fab>
              <Drawer anchor="bottom" open={listOpen} onClose={() => setListOpen(false)}
                      PaperProps={{ sx: { height: '75dvh', borderRadius: '14px 14px 0 0' } }}>
                <ListPanel items={shown} selected={selected} setSelected={setSelected} onOpen={openFromList}
                           userPos={sortKey === 'vicini' ? userPos : null} vedeVoto={vedeVoto} mostraConc={mostraConc} />
              </Drawer>
            </>
          )}
        </>
      )}

      {tab === 'scan' && (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ScanView notify={notify} onDataChanged={loadData} vedeVoto={vedeVoto} />
        </Box>
      )}

      <DetailPanel item={detailItem} onClose={() => setDetailId(null)} onSaved={loadData} notify={notify}
                   selected={selected} setSelected={setSelected} me={me} vedeVoto={vedeVoto}
                   concessionari={concessionari} />

      <PasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} notify={notify} />
      {me.amministratore && (
        <UsersDialog open={usersOpen} me={me} notify={notify}
                     onClose={() => { setUsersOpen(false); caricaConcessionari(); loadData(); }} />
      )}
      <RichiesteEliminazione open={richiesteOpen && richieste.length > 0} onClose={() => setRichiesteOpen(false)}
                             richieste={richieste} onCambiate={loadData} notify={notify}
                             onApri={(id) => { setRichiesteOpen(false); openFromList(id); }} />

      <Snackbar open={!!toast} autoHideDuration={4500} onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : <span />}
      </Snackbar>
    </Stack>
  );
}
